import React, { useEffect, useState } from "react";
import icd10Codes from "../../data/icd10-codes.json";
import { groupCase } from "../../services/drg/index.js";
import { computePayment } from "../../services/billing/money.js";
import { ICD10_AI_HELPER_URL } from "../../config/icd10";

// Demo contract values (replace with Firestore/DB values later)
const HOSPITAL_TCP = 6200;      // lei / caz ponderat (number)
const MONTH_VR_SO_FAR = 380;    // Σ VR luna curentă (number)
const MONTH_VR_CAP    = 400;    // plafon Σ VR / lună (number)

export default function DiagnosticCodeHelper() {
  // search & selection
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [selected, setSelected] = useState([]); // [{code,title_ro,description}]

  // AI
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiSuggestions, setAiSuggestions] = useState([]);

  // DRG / money
  const [drg, setDrg] = useState(null);
  const [vr, setVr] = useState(null);
  const [amount, setAmount] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [groupError, setGroupError] = useState("");

  // Filter local list
  useEffect(() => {
    if (!searchTerm) return setFiltered([]);
    const q = searchTerm.toLowerCase();
    setFiltered(
      icd10Codes.filter(
        (c) =>
          c.code.toLowerCase().includes(q) ||
          (c.title_ro || "").toLowerCase().includes(q) ||
          (c.description || "").toLowerCase().includes(q)
      )
    );
  }, [searchTerm]);

  const addCode = (codeObj) => {
    if (!selected.some((x) => x.code === codeObj.code)) {
      setSelected((s) => [...s, codeObj]);
    }
  };

  const removeCode = (code) => {
    setSelected((s) => s.filter((x) => x.code !== code));
  };

  const copySelected = () => {
    const txt = selected.map((c) => `${c.code} – ${c.title_ro || c.description || ""}`).join(", ");
    navigator.clipboard.writeText(txt);
    alert("Codurile au fost copiate în clipboard.");
  };

  const fetchAi = async () => {
    try {
      setAiError("");
      setAiLoading(true);
      setAiSuggestions([]);

      const res = await fetch(ICD10_AI_HELPER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: aiText.trim() }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.details || `Eroare ${res.status}`);
      }

      const data = await res.json(); // { suggestions: [...] }
      setAiSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
    } catch (e) {
      setAiError(e.message || "Eroare AI.");
    } finally {
      setAiLoading(false);
    }
  };

  const finalizeAndCompute = async () => {
    setGroupError("");
    setDrg(null); setVr(null); setAmount(null); setBreakdown(null);

    if (selected.length === 0) {
      setGroupError("Selectează cel puțin un cod (primul va fi principal).");
      return;
    }

    const principal = selected[0].code;
    const secondary = selected.slice(1).map((c) => c.code);

    try {
      // 1) Group (mock adapter for now)
      const result = await groupCase({ principal, secondary }); // { drg, vr }
      setDrg(result.drg);
      setVr(result.vr);

      // 2) Compute payment (defensive, object-based API)
      const bd = computePayment({
        vr: result.vr,
        tcp: HOSPITAL_TCP,
        monthlyWeightedSoFar: MONTH_VR_SO_FAR,
        monthlyWeightedCap: MONTH_VR_CAP,
      });

      if (!Number.isFinite(bd.base) && !Number.isFinite(bd.cappedBase)) {
        setGroupError("TCP sau VR nu sunt numerice. Verifică valorile.");
        return;
      }

      const payable = bd.capped ? bd.cappedBase : bd.base;
      setAmount(payable);
      setBreakdown(bd);
    } catch (e) {
      setGroupError(e.message || "Eroare la grupare/calcul.");
    }
  };

  const fmt = (n) => Number.isFinite(n) ? n.toLocaleString("ro-RO") : "—";

  return (
    <div className="container">
      <div className="hero card">
        <h2 className="header">Asistent codificare ICD‑10 <span className="pill">DRG</span></h2>
        <div className="subtle">Caută, adaugă din AI, finalizează codarea și vezi decontarea estimată.</div>
      </div>

      {/* NEW — layout pe două coloane (Gestalt: alignment, symmetry) */}
      <div className="grid-gestalt">{/* NEW wrapper grid */}

        {/* NEW — coloana stângă: căutare + rezultate + AI */}
        <div className="col-left stack">{/* NEW start left column */}
          {/* Search */}
          <div className="section card" style={{padding:'14px'}}>
            <input
              className="input"
              type="text"
              placeholder="Caută diagnostic sau cod (ex: I10, diabet)…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Results */}
          {filtered.length > 0 && (
            <div className="section list card">
              {filtered.map((c) => (
                <div
                  key={c.code}
                  className="list-item"
                  onClick={() => addCode(c)}
                  title="Adaugă cod"
                  style={{cursor:'pointer'}}
                >
                  <div>
                    <strong>{c.code}</strong> — {c.title_ro || c.description || ""}
                    <div className="subtle">apăsați pentru a adăuga</div>
                  </div>
                  <button className="btn btn-ghost">➕ Adaugă</button>
                </div>
              ))}
            </div>
          )}

          {/* AI box */}
          <div className="section card" style={{padding:'14px'}}>
            <h4 className="section-title">Sugestie AI (în română)</h4>
            <textarea
              className="textarea"
              rows="4"
              value={aiText}
              placeholder="Descrie simptomele/diagnosticul…"
              onChange={(e) => setAiText(e.target.value)}
            />
            <div style={{display:'flex', gap:8, alignItems:'center', marginTop:10}}>
              <button
                className="btn btn-success"
                onClick={fetchAi}
                disabled={aiLoading || !aiText.trim()}
              >
                {aiLoading ? "⏳ Se încarcă…" : " Sugerează coduri ICD‑10 cu AI"}
              </button>
              {aiError && <span style={{ color: 'crimson', fontSize: 13 }}>{aiError}</span>}
            </div>

            {aiSuggestions.length > 0 && (
              <div className="section list">
                <div className="list-item" style={{background:'var(--accent-100)'}}>
                  <strong>Sugestii AI</strong>
                </div>
                {aiSuggestions.map((s) => (
                  <div key={s.code + s.title_ro} className="list-item">
                    <div>
                      <strong>{s.code}</strong> — {s.title_ro}
                      {s.description ? <div className="subtle" style={{marginTop:4}}>{s.description}</div> : null}
                    </div>
                    <button
                      className="btn btn-success"
                      onClick={() => addCode({ code: s.code, title_ro: s.title_ro, description: s.description })}
                    >
                      ➕ Adaugă
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>{/* NEW end left column */}

        {/* NEW — coloana dreaptă: selectate + finalizează + money panel */}
        <div className="col-right stack">{/* NEW start right column */}
          {/* Selected */}
          {selected.length > 0 && (
            <div className="section card" style={{padding:'14px'}}>
              <h4 className="section-title">Coduri selectate</h4>
              <div className="list">
                {selected.map((c) => (
                  <div key={c.code} className="list-item">
                    <div>{c.code} — {c.title_ro || c.description || ""}</div>
                    <button className="btn btn-danger" onClick={() => removeCode(c.code)}>✖ Elimină</button>
                  </div>
                ))}
              </div>
              <div style={{marginTop:12}}>
                <button className="btn btn-primary" onClick={copySelected}>📋 Copiază în clipboard</button>
              </div>
            </div>
          )}

          {/* Finalize & compute */}
          <div className="section">
            <button className="btn btn-primary" onClick={finalizeAndCompute}>
               Finalizează codarea & calculează suma
            </button>
            {groupError && <span style={{ color: 'crimson', marginLeft:12 }}>{groupError}</span>}
          </div>

          {/* Money panel */}
          {(drg && vr != null && amount != null) && (
            <div className="section money">
              <h4 className="section-title">💰 Calcul plată</h4>
              <div className="kv">
                <div className="box">
                  <div className="label">DRG</div>
                  <div className="value">{drg}</div>
                </div>
                <div className="box">
                  <div className="label">VR</div>
                  <div className="value">{vr}</div>
                </div>
                <div className="box">
                  <div className="label">TCP (lei / caz ponderat)</div>
                  <div className="value">{fmt(HOSPITAL_TCP)}</div>
                </div>
                <div className="box">
                  <div className="label">Σ VR luna curentă</div>
                  <div className="value">{fmt(MONTH_VR_SO_FAR)}</div>
                </div>
                <div className="box">
                  <div className="label">Plafon Σ VR / lună</div>
                  <div className="value">{fmt(MONTH_VR_CAP)}</div>
                </div>
                <div className="box">
                  <div className="label">Suma calculată (lei)</div>
                  <div className="value">{fmt(amount)}</div>
                </div>
              </div>

              {/* Notes from breakdown (cap messages) */}
              {breakdown?.notes?.length ? (
                <ul style={{ marginTop: 12 }} className="subtle">
                  {breakdown.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              ) : null}

              {/* NEW — CTA sticky pe mobil (continuity) */}
              <div className="cta-sticky">
                <button className="btn btn-primary" onClick={finalizeAndCompute}>
                  Recalculează
                </button>
              </div>
            </div>
          )}
        </div>{/* NEW end right column */}

      </div>{/* NEW wrapper grid end */}
    </div>
  );
}